import React, {ReactNode, useEffect} from 'react'
import {
  DeepPartial,
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
  UseFormProps,
  UseFormReturn,
  useWatch
} from 'react-hook-form'

export interface FormProps<T extends FieldValues> {
  children: ReactNode | ((methods: UseFormReturn<T>) => ReactNode);
  onSubmit: SubmitHandler<T>;
  onChange?: SubmitHandler<T>;
  defaultValues: DeepPartial<T>;
  className?: string;
  novalidate?: boolean;
  autocomplete?: boolean;
  name?: string;
  useFormProps?: Omit<UseFormProps<T>, 'defaultValues'>;
}

const Form = <T extends FieldValues>({
  onSubmit,
  children,
  onChange,
  defaultValues,
  useFormProps,
  ...rest
}: FormProps<T>) => {
  const methods = useForm<T>({defaultValues: defaultValues, ...useFormProps})
  const {handleSubmit} = methods

  const useWatchValues = useWatch({
    control: methods.control
  }) /**@TODO subscribe/watch specific fields */

  const conditionalWatchValues = onChange ? useWatchValues : defaultValues

  useEffect(() => {
    if (!onChange) return
    onChange(useWatchValues as T)
  }, [conditionalWatchValues])

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} {...rest}>
        {typeof children === 'function' ? children({...methods}) : children}
      </form>
    </FormProvider>
  )
}

export default Form
